import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import {
  IncludedPickupCommerce,
  IncludedPickupCommercesFilter,
  IncludedPickupCommerceSchema,
} from '../models/IncludedPickupCommerce';

export interface IncludedPickupCommercesResponse {
  count: number;
  items: IncludedPickupCommerce[];
  offset: number;
  limit: number;
}

export interface GetIncludedPickupCommercesArgs {
  limit: number;
  offset: number;
  filters: IncludedPickupCommercesFilter;
}

const includedPickupCommercesService = createApi({
  reducerPath: 'includedPickupCommercesService',
  baseQuery: createBaseQueryWithReauth(API_BUSINESS_URL),
  tagTypes: ['IncludedPickupCommerces'],
  endpoints: (builder) => ({
    getIncludedPickupCommerces: builder.query<
      IncludedPickupCommercesResponse,
      GetIncludedPickupCommercesArgs
    >({
      query: ({ limit, offset, filters }) => {
        const params: Record<string, string | number> = { limit, offset };

        if (filters.commerceId?.trim()) {
          params.commerceId = filters.commerceId.trim();
        }
        if (filters.allowedCommerceId?.trim()) {
          params.allowedCommerceId = filters.allowedCommerceId.trim();
        }
        if (filters.commerceName?.trim()) {
          params.commerceName = filters.commerceName.trim();
        }
        if (filters.allowedCommerceName?.trim()) {
          params.allowedCommerceName = filters.allowedCommerceName.trim();
        }

        return { url: '/included-pickup-commerces', params };
      },
      providesTags: ['IncludedPickupCommerces'],
    }),

    createIncludedPickupCommerce: builder.mutation<
      IncludedPickupCommerce,
      IncludedPickupCommerceSchema
    >({
      query: (body) => ({
        url: '/included-pickup-commerces',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['IncludedPickupCommerces'],
    }),

    deleteIncludedPickupCommerce: builder.mutation<
      void,
      { commerceId: string; allowedCommerceId: string }
    >({
      query: ({ commerceId, allowedCommerceId }) => ({
        url: `/included-pickup-commerces/${commerceId}/${allowedCommerceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['IncludedPickupCommerces'],
    }),
  }),
});

export default includedPickupCommercesService;

export const {
  useGetIncludedPickupCommercesQuery,
  useCreateIncludedPickupCommerceMutation,
  useDeleteIncludedPickupCommerceMutation,
} = includedPickupCommercesService;
